import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, X } from "lucide-react";
import Pill from "@/components/Pill";
import FileUpload from "@/components/FileUpload";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { can, isResourceReadOnly, resourceFromEndpoint, useEnabledModules } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";

/**
 * EntityPage — generic list + drawer create/edit
 *
 * Props:
 *  - title, subtitle, testidPrefix, endpoint (e.g., "/projects")
 *  - columns: [{ key, label, render?: (item) => ReactNode }]
 *  - fields: [{ key, label, type, options?, required?, full? }]
 *      type: text, textarea, email, date, select, contacts, files, multiselect (members ids)
 *  - initialForm: default form state
 *  - referenceData: optional context (projects, teams, etc.)
 */
export default function EntityPage({
  title,
  singularTitle,
  subtitle,
  headerExtra,
  testidPrefix,
  endpoint,
  extraParams,
  columns,
  fields,
  initialForm,
  referenceData = {},
  filters,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [activeFilters, setActiveFilters] = useState({});
  const { user } = useAuth();
  const moduleMeta = useEnabledModules();
  const resource = resourceFromEndpoint(endpoint);
  const readOnly = isResourceReadOnly(moduleMeta, resource);
  // Role-based perms gate. Module-level Read Only overrides ALL write actions
  // for non-admin users (admins keep full access — read-only is a per-user toggle).
  const canCreate = !readOnly && can(user, resource, "create");
  const canUpdate = !readOnly && can(user, resource, "update");
  const canDelete = !readOnly && can(user, resource, "delete");

  const load = async () => {
    setLoading(true);
    try {
      const params = { ...(extraParams || {}) };
      if (search) params.q = search;
      const { data } = await api.get(endpoint, { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // initial fetch only

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [search, JSON.stringify(extraParams)]); // debounce search & refetch on param change

  const startCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setOpen(true);
  };

  const startEdit = (item) => {
    setEditing(item);
    // copy item into form shape
    const next = { ...initialForm };
    Object.keys(initialForm).forEach((k) => {
      if (item[k] !== undefined && item[k] !== null) next[k] = item[k];
    });
    setForm(next);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Clean payload
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (editing) {
        await api.patch(`${endpoint}/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(endpoint, payload);
        toast.success("Created");
      }
      setOpen(false);
      load();
      // Refresh shared reference data so newly created users/teams/etc. appear
      // immediately in dropdowns across the app.
      referenceData?.refresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`${endpoint}/${confirmDel.id}`);
      toast.success("Deleted");
      setConfirmDel(null);
      load();
      referenceData?.refresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid={`${testidPrefix}-page`}>
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white" style={{ fontFamily: "Outfit" }}>
            {title}
          </h1>
          {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
          {headerExtra}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="pl-9 w-64 bg-zinc-900 border-white/10"
              data-testid={`${testidPrefix}-search`}
            />
          </div>
          <Button
            onClick={startCreate}
            className="bg-white text-black hover:bg-zinc-200 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`${testidPrefix}-create-btn`}
            disabled={!canCreate}
            title={canCreate ? "" : "You don't have permission to create"}
          >
            <Plus className="w-4 h-4 mr-1.5" /> New
          </Button>
        </div>
      </header>

      {filters && filters.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid={`${testidPrefix}-filters`}>
          <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium mr-1">Filters</span>
          {filters.map((f) => {
            const active = !!activeFilters[f.key];
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setActiveFilters((p) => ({ ...p, [f.key]: !p[f.key] }))}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                  active
                    ? "bg-white text-black border-white"
                    : "bg-zinc-900 text-zinc-300 border-white/10 hover:border-white/30"
                }`}
                data-testid={`${testidPrefix}-filter-${f.key}`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="w-full overflow-hidden border border-white/10 rounded-lg bg-zinc-900">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-950/50 border-b border-white/10 text-left">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className="px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 font-medium"
                >
                  {c.label}
                </th>
              ))}
              <th className="px-4 py-3 w-1" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-zinc-500 text-sm">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="px-4 py-12 text-center text-zinc-500 text-sm">
                No items yet. Click <span className="text-zinc-300">New</span> to add one.
              </td></tr>
            ) : (() => {
              const visibleItems = (filters || []).reduce(
                (arr, f) => (activeFilters[f.key] ? arr.filter(f.predicate) : arr),
                items
              );
              if (visibleItems.length === 0) {
                return (
                  <tr><td colSpan={columns.length + 1} className="px-4 py-12 text-center text-zinc-500 text-sm">
                    No items match the active filters.
                  </td></tr>
                );
              }
              return visibleItems.map((it) => (
                <tr
                  key={it.id}
                  className="border-b border-white/5 hover:bg-white/[0.025] transition-colors"
                  data-testid={`${testidPrefix}-row-${it.id}`}
                >
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 text-zinc-300">
                      {c.render ? c.render(it) : (it[c.key] ?? <span className="text-zinc-600">—</span>)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {canUpdate && (
                      <button
                        onClick={() => startEdit(it)}
                        className="p-1.5 text-zinc-500 hover:text-white hover:bg-white/5 rounded mr-1"
                        data-testid={`${testidPrefix}-edit-${it.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => setConfirmDel(it)}
                        className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-white/5 rounded"
                        data-testid={`${testidPrefix}-delete-${it.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {!canUpdate && !canDelete && (
                      <span className="text-xs text-zinc-700">—</span>
                    )}
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </div>

      {/* Drawer Form */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="bg-zinc-950 border-l border-white/10 text-zinc-100 w-full sm:max-w-xl overflow-y-auto"
          data-testid={`${testidPrefix}-drawer`}
        >
          <SheetHeader>
            <SheetTitle className="text-white" style={{ fontFamily: "Outfit" }}>
              {editing ? `Edit ${singularTitle || title.slice(0, -1) || title}` : `New ${singularTitle || title.slice(0, -1) || title}`}
            </SheetTitle>
            {editing && editing.code && (
              <div className="text-xs text-zinc-500 font-mono mt-1">{editing.code}</div>
            )}
          </SheetHeader>

          <div className="mt-6 grid grid-cols-2 gap-4">
            {fields.map((f) => (
              <Field
                key={f.key}
                field={f}
                value={form[f.key]}
                form={form}
                onChange={(v) => setForm({ ...form, [f.key]: v })}
                referenceData={referenceData}
                testidPrefix={testidPrefix}
                editing={!!editing}
              />
            ))}
          </div>

          <div className="mt-8 flex items-center justify-end gap-2 pb-4">
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-zinc-300 hover:text-white">
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-white text-black hover:bg-zinc-200 font-medium"
              data-testid={`${testidPrefix}-save-btn`}
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Create"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-zinc-950 border border-white/10 text-zinc-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this item?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-white/10 text-zinc-200 hover:bg-zinc-800">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid={`${testidPrefix}-confirm-delete`}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ field, value, form, onChange, referenceData, testidPrefix, editing }) {
  const full = field.full;
  const wrapperCls = full ? "col-span-2" : "col-span-2 md:col-span-1";
  const tid = `${testidPrefix}-field-${field.key}`;
  const locked = !!(field.disabledOnEdit && editing);

  if (field.type === "textarea") {
    return (
      <div className={wrapperCls}>
        <Label className="text-xs text-zinc-400">{field.label}</Label>
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1.5 bg-zinc-900 border-white/10 text-white min-h-[90px]"
          data-testid={tid}
        />
      </div>
    );
  }

  if (field.type === "select") {
    let opts = field.optionsRef ? referenceData[field.optionsRef] || [] : field.options || [];
    if (field.filterBy && form) {
      const filterValue = form[field.filterBy];
      if (!filterValue) {
        opts = []; // no parent selected → no options
      } else {
        opts = opts.filter((o) =>
          typeof o === "string" ? true : o[field.filterBy] === filterValue
        );
      }
    }
    const filterLabel = field.filterBy === "project_id" ? "CDARK" : (field.filterBy || "").replace("_", " ");
    const placeholder =
      field.filterBy && form && !form[field.filterBy]
        ? `Select ${filterLabel} first`
        : "Select…";
    return (
      <div className={wrapperCls}>
        <Label className="text-xs text-zinc-400">{field.label}</Label>
        <Select value={value || ""} onValueChange={(v) => onChange(v)}>
          <SelectTrigger className="mt-1.5 bg-zinc-900 border-white/10 text-white" data-testid={tid}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100 max-h-72">
            {opts.length === 0 ? (
              <div className="px-2 py-3 text-xs text-zinc-500">
                {field.filterBy && form && !form[field.filterBy]
                  ? `Pick a ${filterLabel} first`
                  : "No options"}
              </div>
            ) : (
              opts.map((opt) => {
                const v = typeof opt === "string" ? opt : opt.value;
                const lbl = typeof opt === "string" ? opt : opt.label;
                return (
                  <SelectItem key={v} value={v}>
                    {lbl}
                  </SelectItem>
                );
              })
            )}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.type === "contacts") {
    const list = Array.isArray(value) ? value : [];
    const update = (idx, k, v) => {
      const next = [...list];
      next[idx] = { ...next[idx], [k]: v };
      onChange(next);
    };
    const add = () => onChange([...list, { name: "", email: "", phone: "" }]);
    const rm = (idx) => onChange(list.filter((_, i) => i !== idx));
    return (
      <div className="col-span-2">
        <Label className="text-xs text-zinc-400">{field.label}</Label>
        <div className="mt-2 space-y-2">
          {list.map((c, i) => (
            <div key={i} className="grid grid-cols-12 gap-2" data-testid={`${tid}-row-${i}`}>
              <Input value={c.name || ""} onChange={(e) => update(i, "name", e.target.value)} placeholder="Name" className="col-span-4 bg-zinc-900 border-white/10 text-white" />
              <Input value={c.email || ""} onChange={(e) => update(i, "email", e.target.value)} placeholder="Email" className="col-span-4 bg-zinc-900 border-white/10 text-white" />
              <Input value={c.phone || ""} onChange={(e) => update(i, "phone", e.target.value)} placeholder="Phone" className="col-span-3 bg-zinc-900 border-white/10 text-white" />
              <button type="button" onClick={() => rm(i)} className="col-span-1 text-zinc-500 hover:text-red-400 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <Button type="button" variant="ghost" size="sm" onClick={add} className="text-zinc-300 hover:text-white" data-testid={`${tid}-add`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add contact
          </Button>
        </div>
      </div>
    );
  }

  if (field.type === "files") {
    return (
      <div className="col-span-2">
        <Label className="text-xs text-zinc-400">{field.label}</Label>
        <div className="mt-2">
          <FileUpload value={value || []} onChange={onChange} testid={tid} />
        </div>
      </div>
    );
  }

  if (field.type === "multiselect") {
    const opts = referenceData[field.optionsRef] || [];
    const list = Array.isArray(value) ? value : [];
    const remove = (id) => onChange(list.filter((x) => x !== id));
    const add = (id) => {
      if (!id || list.includes(id)) return;
      onChange([...list, id]);
    };
    const selectedOpts = list
      .map((id) => opts.find((o) => o.value === id))
      .filter(Boolean);
    const available = opts.filter((o) => !list.includes(o.value));

    return (
      <div className="col-span-2 space-y-3">
        <Label className="text-xs text-zinc-400">{field.label}</Label>

        {/* Current members */}
        <div className="p-3 bg-zinc-900 border border-white/10 rounded-md min-h-[3rem]">
          {selectedOpts.length === 0 ? (
            <span className="text-xs text-zinc-600">No members added yet.</span>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid={`${tid}-selected`}>
              {selectedOpts.map((o) => (
                <span
                  key={o.value}
                  className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full text-xs bg-white text-black border border-white"
                  data-testid={`${tid}-chip-${o.value}`}
                >
                  {o.label}
                  <button
                    type="button"
                    onClick={() => remove(o.value)}
                    className="p-0.5 hover:bg-black/10 rounded-full"
                    aria-label={`Remove ${o.label}`}
                    data-testid={`${tid}-remove-${o.value}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Add member dropdown */}
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-zinc-500 uppercase tracking-wider shrink-0">
            Add user
          </span>
          <Select value="" onValueChange={(v) => add(v)} disabled={available.length === 0}>
            <SelectTrigger
              className="bg-zinc-900 border-white/10 text-white flex-1"
              data-testid={`${tid}-add-trigger`}
            >
              <SelectValue placeholder={available.length === 0 ? "All users added" : "Pick a user to add…"} />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100 max-h-72">
              {available.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`${tid}-opt-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperCls}>
      <Label className="text-xs text-zinc-400">
        {field.label}
        {field.required && <span className="text-red-400 ml-1">*</span>}
        {locked && <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-600">(locked)</span>}
      </Label>
      <Input
        type={field.type || "text"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={locked}
        readOnly={locked}
        className={`mt-1.5 bg-zinc-900 border-white/10 text-white ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
        placeholder={field.placeholder}
        data-testid={tid}
      />
    </div>
  );
}
